import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ClientDetails,
  PtdClientService,
  ShipmentCompletion,
  WarehousePacked,
  PtdProformaService,
  SplitWarehouse,
  PtdDispatchDetails,
  FreightMethod,
  PtdDispatchService,
  PreDispatchDetails,
  Proforma,
  Warehouse,
  DeadlineUpdate,
  DispatchWarehouseDetails,
} from 'coadapi';
import { BehaviorSubject, forkJoin, Observable } from 'rxjs';
import { DomSanitizer } from '@angular/platform-browser';
import {
  CdkDragDrop, transferArrayItem,
} from '@angular/cdk/drag-drop';
import { MatDialog as MatDialog, MatDialogRef as MatDialogRef } from '@angular/material/dialog';
import { startWith, switchMap, tap } from 'rxjs/operators';
import { 
  ClientActionService, 
  DialogCompleteService, 
  SharedDispatchData, 
  SharedProformaData, 
  sharedUserData, 
  StateService 
} from '../../abstracts/_datasharing';
import Utils from '../../abstracts/_utils';
import { downloadSummaries } from '../../abstracts/_download-summaries';
import { MatIconRegistry } from '@angular/material/icon';
import { ConfirmationComponent } from '../../../dialogs/confirmation/confirmation.component';
import { IwdDeadlineComponent } from '../../../dialogs/iwd-deadline/iwd-deadline.component';
import { CustomDateAdapter } from '../../abstracts/_date-adapter';

export class TransferArrayComponent {
  origArray: Array<any> = [];
  targArray: Array<any> = [];
  origIndex = 0;
  targIndex = 0;
}

@Component({
  selector: 'app-client',
  templateUrl: './client.component.html',
  styleUrls: ['./client.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClientComponent implements OnInit {
  selectedClient$!: Observable<ClientDetails | null>;
  selectedClientId = 0;
  selectedDispatch$!: Observable<PtdDispatchDetails>;
  hasProformaMovedSwitch = false;
  flipState = new Map<number, boolean>();
  reloadState = new Map<number, number>();
  isClosed = false;
  dialogRef!: MatDialogRef<any>;
  passedClientAndDispatch = new Map<number, any>();
  ukStateColorMap = new Map<number, string>();
  usStateColorMap = new Map<number, string>();
  stateColorMap = new Map<number, string>();
  expansionState = new Map<number, boolean>();
  firstBoot = true;
  sacrificialArray: SplitWarehouse[] = [];
  dispatchDropArray: string[] = [];
  dispatchDropCreateArray: string[] = [];
  iwdReturn: any[] = [];
  private refreshDebounceTimeout?: ReturnType<typeof setTimeout>;
  private clientState: ClientDetails | null = null;
  private clientStateSubject = new BehaviorSubject<ClientDetails | null>(null);
  private proformaCache = new Map<number, Proforma>();

  private toDispatchIsoDate(date: Date): string {
    return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())).toISOString();
  }

  constructor(
    private dialog: MatDialog,
    private route: ActivatedRoute,
    private router: Router,
    private clientService: PtdClientService,
    public proformaService: PtdProformaService,
    public sanitizer: DomSanitizer,
    private sharedDispatchData: SharedDispatchData,
    private sharedUserData: sharedUserData,
    private sharedProformaData: SharedProformaData,
    private dispatchService: PtdDispatchService,
    public stateService: StateService,
    public clientActionService: ClientActionService,
    public dialogCompleteService: DialogCompleteService,
    private downloadSummaries: downloadSummaries,
    iconRegistry: MatIconRegistry
  ) 
  { 
    iconRegistry.addSvgIcon('looking-glass', sanitizer.bypassSecurityTrustResourceUrl('app/coad-web/proforma-to-dispatch/images/contentssvg.svg')),
    iconRegistry.addSvgIcon('edit-pen', sanitizer.bypassSecurityTrustResourceUrl('app/coad-web/proforma-to-dispatch/images/editsvg.svg')),
    iconRegistry.addSvgIcon('red-corner', sanitizer.bypassSecurityTrustResourceUrl('app/coad-web/proforma-to-dispatch/images/red-corner.svg')),
    iconRegistry.addSvgIcon('break-link', sanitizer.bypassSecurityTrustResourceUrl('app/coad-web/proforma-to-dispatch/images/disassociate.svg')),
    iconRegistry.addSvgIcon('trash-can', sanitizer.bypassSecurityTrustResourceUrl('app/coad-web/proforma-to-dispatch/images/trashsvg.svg')),
    iconRegistry.addSvgIcon('report', sanitizer.bypassSecurityTrustResourceUrl('app/coad-web/proforma-to-dispatch/images/report-final.svg')),
    iconRegistry.addSvgIcon('expand', sanitizer.bypassSecurityTrustResourceUrl('app/coad-web/proforma-to-dispatch/images/expand.svg')),
    iconRegistry.addSvgIcon('avatar', sanitizer.bypassSecurityTrustResourceUrl('app/coad-web/proforma-to-dispatch/images/avatar.svg')),
    iconRegistry.addSvgIcon('marker-agent', sanitizer.bypassSecurityTrustResourceUrl('app/coad-web/proforma-to-dispatch/images/marker-agent.svg')),
    iconRegistry.addSvgIcon('marker-primary', sanitizer.bypassSecurityTrustResourceUrl('app/coad-web/proforma-to-dispatch/images/marker-primary.svg'))
    // subscribe to the dialogcomplete subject to watch for the completed dialog close
    this.dialogCompleteService.dialogComplete$.subscribe({
      next: (result: boolean) => {
        if (result === true) {
          this.scheduleClientRefresh();
        }
      }
    })
  }

  private getCurrentContactId(): number {
    return this.sharedUserData.getUser()?.ContactID ?? 0;
  }

  scheduleClientRefresh(): void {
    if (!this.clientActionService?.clientAction$) {
      return;
    }

    if (this.refreshDebounceTimeout) {
      clearTimeout(this.refreshDebounceTimeout);
    }

    this.refreshDebounceTimeout = setTimeout(() => {
      this.refreshDebounceTimeout = undefined;
      this.refreshClientFromServer();
    }, 1000);
  }

  getIncotermLocation(details: { incotermLocation?: string; incotermlocation?: string }): string {
    const location = details.incotermLocation ?? details.incotermlocation;
    return typeof location === 'string' ? location.trim() : '';
  }

  private refreshClientFromServer(): void {
    if (!Number.isFinite(this.selectedClientId)) {
      return;
    }

    this.clientService.getOneClient(this.selectedClientId).subscribe((res) => {
      this.applyClientState(res);
    });
  }

  private applyOptimisticAssociationState(
    splitWarehouse: SplitWarehouse,
    previousDispatchId: number | null,
    targetDispatchId: number,
    sourceItem?: SplitWarehouse
  ): void {
    if (!this.clientState) {
      return;
    }

    const draggedItem = sourceItem ?? splitWarehouse;
    const sourceWarehouse = draggedItem?.warehouse ?? null;
    const safeWarehouse = sourceWarehouse && typeof sourceWarehouse.warehouseId === 'number'
      ? sourceWarehouse
      : { warehouseId: 0, region: 'Unknown' } as any;

    const matchesDraggedSplit = (item: any): boolean => {
      if (!item || item.originPfi !== (draggedItem?.originPfi ?? splitWarehouse.originPfi)) {
        return false;
      }

      const itemWarehouseId = item.warehouse?.warehouseId ?? 0;
      return itemWarehouseId === (safeWarehouse?.warehouseId ?? 0);
    };

    const nextDispatches = (this.clientState.dispatches ?? []).map((dispatch) => {
      if (dispatch.dispatchId === previousDispatchId) {
        return {
          ...dispatch,
          dispatchProformas: (dispatch.dispatchProformas ?? []).filter((item) => !matchesDraggedSplit(item)),
        };
      }

      if (dispatch.dispatchId === targetDispatchId) {
        const existing = (dispatch.dispatchProformas ?? []).find(matchesDraggedSplit);

        const optimisticItem = {
          ...(existing ?? draggedItem ?? {}),
          ...draggedItem,
          associatedDispatch: targetDispatchId,
          warehouse: draggedItem?.warehouse ?? existing?.warehouse ?? safeWarehouse,
          splitCommodity: draggedItem?.splitCommodity ?? existing?.splitCommodity,
          warehouseLineCount: draggedItem?.warehouseLineCount ?? existing?.warehouseLineCount,
          warehouseValue: draggedItem?.warehouseValue ?? existing?.warehouseValue,
        };

        const nextItems = existing
          ? (dispatch.dispatchProformas ?? []).map((item) => matchesDraggedSplit(item) ? optimisticItem : item)
          : [...(dispatch.dispatchProformas ?? []), optimisticItem];

        return {
          ...dispatch,
          dispatchProformas: nextItems,
        };
      }

      return dispatch;
    });

    const nextProformas = (this.clientState.proformas ?? []).map((proforma) => {
      if (proforma.pfiId !== draggedItem.originPfi) {
        return proforma;
      }

      return {
        ...proforma,
        pfiSplit: (proforma.pfiSplit ?? []).filter((item) => !matchesDraggedSplit(item)),
      };
    });

    this.clientState = {
      ...this.clientState,
      dispatches: nextDispatches,
      proformas: nextProformas,
    };
    this.clientStateSubject.next(this.clientState);
  }

  private applyOptimisticDisassociationState(
    splitWarehouses: SplitWarehouse[],
    previousDispatchId: number | null
  ): void {
    if (!this.clientState) {
      return;
    }

    const matchesReturnedSplit = (item: any): boolean => splitWarehouses.some((splitWarehouse) =>
      item.originPfi === splitWarehouse.originPfi &&
      (item.warehouse?.warehouseId ?? 0) === (splitWarehouse.warehouse?.warehouseId ?? 0)
    );

    const nextDispatches = (this.clientState.dispatches ?? []).map((dispatch) => {
      if (dispatch.dispatchId !== previousDispatchId) {
        return dispatch;
      }

      return {
        ...dispatch,
        dispatchProformas: (dispatch.dispatchProformas ?? []).filter((item) => !matchesReturnedSplit(item)),
      };
    });

    const sourceProformaIndex = (this.clientState.proformas ?? []).findIndex(
      (proforma) => proforma.pfiId === splitWarehouses[0]?.originPfi
    );
    const nextProformas = (this.clientState.proformas ?? []).map((proforma) => {
      if (proforma.pfiId !== splitWarehouses[0]?.originPfi) {
        return proforma;
      }

      return {
        ...proforma,
        pfiSplit: [...(proforma.pfiSplit ?? []), ...splitWarehouses],
      };
    });

    if (sourceProformaIndex < 0 && typeof splitWarehouses[0]?.originPfi === 'number') {
      const cachedProforma = this.proformaCache.get(splitWarehouses[0].originPfi);
      if (cachedProforma) {
        nextProformas.push({
          ...cachedProforma,
          pfiSplit: splitWarehouses,
        });
      }
    }

    this.clientState = {
      ...this.clientState,
      dispatches: nextDispatches,
      proformas: nextProformas,
    };
    this.clientStateSubject.next(this.clientState);
  }

  private applyClientState(res: ClientDetails): void {
    (res.proformas ?? []).forEach((proforma) => {
      if (typeof proforma.pfiId === 'number') {
        this.proformaCache.set(proforma.pfiId, proforma);
      }
    });
    this.clientState = res;
    this.clientStateSubject.next(res);

    if (this.firstBoot) {
      (res.dispatches ?? []).forEach((dispatch) => {
        if (typeof dispatch.dispatchId === 'number') {
          this.expansionState.set(dispatch.dispatchId, false);
          this.flipState.set(dispatch.dispatchId, false);
        }
      });
      this.stateService.drawerState$.next(this.expansionState);
      this.stateService.flipState$.next(this.flipState);
      this.firstBoot = false;
    }

    this.iwdReturn = [];
    (res.dispatches ?? []).forEach((dispatch) => {
      (dispatch.dispatchWarehouseDetails ?? []).forEach((dw) => {
        const warehouseId = dw.warehouse?.warehouseId;
        if (typeof warehouseId === 'number') {
          this.iwdReturn.push([dispatch.dispatchId, this.calculateIWDPieValues(dw, warehouseId)]);
        }
      });
    });
    console.log(res);
  }

  ngOnInit() {
    this.selectedClient$ = this.clientStateSubject.asObservable();

    this.route.paramMap.subscribe((params) => {
      this.selectedClientId = Number(params.get('clientId'));
      this.refreshClientFromServer();
    });

    this.ukStateColorMap.set(0, 'hsl(207, 57%, 85%)'); /// EMS Light Blue
    this.ukStateColorMap.set(1, 'hsl(207, 57%, 75%)'); /// EMS Light Blue Monochromatic
    this.usStateColorMap.set(0, 'hsl(0, 82%, 78%)'); /// EMS Light Red
    this.usStateColorMap.set(1, 'hsl(0, 82%, 68%)'); /// EMS Light Red Monochromatic

  }

  getIndex(iwdReturn: any[], dispatchId: number, warehouseId: number): number {
    if (!Array.isArray(iwdReturn) || typeof warehouseId !== 'number') {
      return -1;
    }
    return iwdReturn.findIndex(x => x[0] === dispatchId && x[1]?.warehouseId === warehouseId);
  }

  dispatchIsClosed(isClosed: boolean): string | undefined {
    if (isClosed) {
      return 'isclosed';
    }
    return undefined;
  }

  hasConsignee(consignee: number, delivery: number): string | undefined {
    if (consignee !== delivery) {
      return 'has-consignee';
    }
    return undefined;
  }

  getColor(warehouseId: number) {
    if (warehouseId === 1) {
      return this.ukStateColorMap;
    } else {
      return this.usStateColorMap;
    }
  }

  getPrimary(warehousePackedCount: any[], dispatch: any): WarehousePacked | undefined {
    let primaryPacked: WarehousePacked | undefined;
    warehousePackedCount.forEach((element: WarehousePacked) => {
      if (element.warehouseId === dispatch.dispatchWarehouse.warehouseId) {
        primaryPacked = element;
      }
    });
    return primaryPacked;
  }

  getSecondary(warehousePackedCount: any[], dispatch: any): WarehousePacked | undefined {
    let secondaryPacked: WarehousePacked | undefined;
    warehousePackedCount.forEach((element: WarehousePacked) => {
      if (element.warehouseId !== dispatch.dispatchWarehouse.warehouseId) {
        secondaryPacked = element;
      }
    });
    return secondaryPacked;
  }

  openContentDialog(destination: number): void {
    this.router.navigate(['contents'], {
      relativeTo: this.route,
      skipLocationChange: true,
      queryParams: {
        destination: destination
      }
    });
  }

  openIwdContentDialog(dispatch: number): void {
    this.router.navigate(['iwd-contents'], {
      relativeTo: this.route,
      skipLocationChange: true,
      queryParams: {
        dispatchId: dispatch
      }
    });
  }

  /**
 * Opens the dispatch dialog.
 * 
 * @param dispatch - The dispatch details.
 * @param client - The client details to pull ID, preferred agent, split shipments and descriptive from.
 * @param contactId - The contact ID of the user.
 * @param splitWarehouse - The split warehouse for use when we're dragging a proforma to create a dispatch. [optional] 
 */
  openDispatchDialog(dispatch: PtdDispatchDetails, client: ClientDetails, contactId: number, splitWarehouse?: SplitWarehouse): void {
    this.router.navigate(['dispatch'], {
      relativeTo: this.route,
      skipLocationChange: true,
      state: {
        ptdDispatch: dispatch,
        clientDetails: client,
        contactId: contactId,
        splitWarehouse: splitWarehouse ?? null,
        currentLast: this.getHighestDispatchNumber(client),
        // freightMethod: originProforma?.pfiFreightMethod ?? dispatch.dispatchFreightMethod
      }
    });
  }

  getHighestDispatchNumber(clientDetails: ClientDetails): number | null {
    if (!clientDetails.dispatches || clientDetails.dispatches.length === 0) {
        return null; // No dispatches available
    }
    const dispatchNumbers = clientDetails.dispatches
      .map((dispatch) => dispatch.dispatchNo)
      .filter((dispatchNo): dispatchNo is number => typeof dispatchNo === 'number');

    return dispatchNumbers.length ? Math.max(...dispatchNumbers) : null;
  }

  openIWDDispatchDialog(
    originDispatch: PtdDispatchDetails, 
    client: ClientDetails,
    dispatch: PtdDispatchDetails
  ) {
    this.sharedDispatchData.setData(client, dispatch);
    this.sharedDispatchData.setIWDData(client, originDispatch, dispatch);
    this.router.navigate(['dispatch'], {
      relativeTo: this.route,
      skipLocationChange: true,
    });
  }

  /**
   * Opens the IWD deadline dialog.
   * 
   * @param originDispatch - The dispatch details.
   * @param iwds - The array of IWD split warehouses.
   */
  openIWDDeadlineDialog(
    originDispatch: PtdDispatchDetails,
    iwds: Array<SplitWarehouse>
  ) {
    const originDispatchId = originDispatch.dispatchId;
    if (typeof originDispatchId !== 'number') {
      return;
    }

    // Get the dispatch warehouses
    this.dispatchService
      .getDispatchWarehouses(originDispatchId)
      .subscribe((result: any) => {
        const originWarehouseId = originDispatch.dispatchWarehouse?.warehouseId ?? 0;
        // Filter the duplicate warehouses
        const distinctWarehouses = result.filter(
          (warehouse: any, index: number, self: any[]) =>
            index === self.findIndex((t: any) => t.warehouseId === warehouse.warehouseId)
        );

        // If there is at least one warehouse
        if (distinctWarehouses.length >= 1) {
          // Open the IWD deadline dialog
          const iwdDeadlineRef = this.dialog.open(IwdDeadlineComponent, {
            id: "iwd-deadline-dialog",
            width: "600px",
            disableClose: false,
            panelClass: "confirmation-pane",
            data: {
              // Pass the distinct warehouses, dispatch and warehouse
              distinctWarehouses: distinctWarehouses,
              dispatch: originDispatch,
              warehouse: originWarehouseId,
            },
          });

          // When the dialog is closed
          iwdDeadlineRef.afterClosed().subscribe((res) => {
            // If the user made a selection
            if (res) {
              // Iterate over the selections
              res.forEach((element: any) => {
                // If the selection is not for the origin warehouse
                if (element.index > 0) {
                  // Create the new IWD deadline
                  const utcEts = CustomDateAdapter.changeDateToFlatDate(
                    element.selectedRangeValue.start
                  );
                  const utcEta = CustomDateAdapter.changeDateToFlatDate(
                    element.selectedRangeValue.end
                  );
                  const iwdDeadline = <DeadlineUpdate>{
                    dispatchId: originDispatch.dispatchId,
                    warehouseId: element.index,
                    ets: this.toDispatchIsoDate(utcEts),
                    eta: this.toDispatchIsoDate(utcEta),
                  };

                  // Update the IWD deadline
                  this.dispatchService
                    .setIWDDeadline(iwdDeadline)
                    .subscribe({
                      next: (result: any) => {
                        console.log(result);
                        this.scheduleClientRefresh();
                      },
                      complete: () => {
                        console.log("update complete");
                        return true;
                      },
                    });
                }
              });
            }
          });
        }
      });
  }

  countIwdWarehouses(
    iwds: Array<SplitWarehouse>,
    originWarehouse: number
  ): Array<Warehouse> {
    let distinctWarehouse: Array<Warehouse> = [];
    iwds.forEach((iwd) => {
      const warehouse = iwd.warehouse;
      if (!warehouse || typeof warehouse.warehouseId !== 'number') {
        return;
      }

      if (warehouse.warehouseId !== 5) {
        if (
          !distinctWarehouse.find(
            (x) => x.warehouseId === warehouse.warehouseId
          )
        ) {
          if (warehouse.warehouseId === originWarehouse) {
            distinctWarehouse.splice(0, 0, warehouse);
          } else {
            distinctWarehouse.push(warehouse);
          }
        }
      }
    });
    return distinctWarehouse;
  }

  /**
   * Opens the dispatch dialog with the given parameters
   * @param dispatch The dispatch details
   * @param client The client details
   * @param contactId The contact id of the dispatch (optional)
   * @param splitWarehouseProforma The split warehouse proforma (optional)
   */
  sharingDispatchData(
    client: ClientDetails,
    dispatch: PtdDispatchDetails,
    destination: number,
    contactId?: number,
    splitWarehouseProforma?: SplitWarehouse
  ) {
    this.sharedDispatchData.setData(client, dispatch);
    if (destination === 1) {
      // Open the dispatch dialog
      const resolvedContactId = typeof contactId === 'number' ? contactId : this.getCurrentContactId();
      if (typeof resolvedContactId === 'number') {
        this.openDispatchDialog(dispatch, client, resolvedContactId, splitWarehouseProforma);
      }
    } else if (destination === 2) {
      // Open the dispatch contents dialog
      this.openContentDialog(1);
    } else if (destination === 3) {
      // Open the iwd contents dialog
      if (typeof dispatch.dispatchId === 'number') {
        this.openIwdContentDialog(dispatch.dispatchId);
      }
    }
  }

  sharingProformaData(
    proforma: Proforma
  ) {
    this.sharedProformaData.setData(proforma);
    this.openContentDialog(2);
  }

  public calculatePieValues(completion: ShipmentCompletion) {
    const warehousePackedCount = completion.warehousePackedCount ?? [];
    const dispatchLinecount = completion.dispatchLinecount ?? 1;
    let runningTotal = 0;
    return warehousePackedCount.map(
      (
        currentValue: WarehousePacked,
        index: number,
        internalWarehousePacked: WarehousePacked[]
      ) => {
        const previous = index > 0 ? internalWarehousePacked[index - 1] : undefined;
        const results = {
          start:
            ((runningTotal + (index === 0 ? 0 : (previous?.packed ?? 0) + (previous?.unpacked ?? 0))) /
              dispatchLinecount) *
            100,
          packed: ((currentValue.packed ?? 0) / dispatchLinecount) * 100,
          unpacked:
            ((currentValue.unpacked ?? 0) / dispatchLinecount) * 100,
          over50packed:
            ((currentValue.packed ?? 0) / dispatchLinecount) * 100 > 50
              ? 1
              : 0,
          over50unpacked:
            ((currentValue.unpacked ?? 0) / dispatchLinecount) * 100 > 50
              ? 1
              : 0,
          percentRecieved:
            (((currentValue.packed ?? 0) + (currentValue.unpacked ?? 0)) /
              dispatchLinecount) *
            100,
        };
        runningTotal +=
          (internalWarehousePacked[index]?.packed ?? 0) +
          (internalWarehousePacked[index]?.unpacked ?? 0);
        return results;
      }
    );
  }

  public calculateIWDPieValues(dwDetails: DispatchWarehouseDetails, warehouseId: number) {
    if (!dwDetails || !Array.isArray(dwDetails.warehousePackedCount) || dwDetails.warehousePackedCount.length === 0) {
      return null;
    }

    const relIndex = dwDetails.warehousePackedCount.findIndex((x) => x.warehouseId === warehouseId);
    if (relIndex < 0) {
      return null;
    }

    const packedEntry = dwDetails.warehousePackedCount[relIndex];
    if (!packedEntry) {
      return null;
    }

    const lineCount = dwDetails.warehouseLineCount || 1;
    const packed = packedEntry.packed ?? 0;
    const unpacked = packedEntry.unpacked ?? 0;
    const results = {
      warehouseId: warehouseId,
      start: 0,
      packed:
        (packed /
          lineCount) *
        100,
      unpacked:
        (unpacked /
          lineCount) *
        100,
      over50packed:
        (packed /
          lineCount) *
          100 >
        50
          ? 1
          : 0,
      over50unpacked:
        (unpacked /
          lineCount) *
          100 >
        50
          ? 1
          : 0,
      percentRecieved:
        ((packed +
          unpacked) /
          lineCount) *
        100,
    };
    return results;
  }

  public addId(dispatchId: number) {
    if (this.dispatchDropArray.findIndex((x: string) => x.includes(String(dispatchId))) < 0) {
      if (dispatchId === 0) {
        this.dispatchDropArray.push('dropCreate');
        return 'dropCreate';
      } else {
        this.dispatchDropArray.push('cdk-drop-list-' + dispatchId);
        this.dispatchDropArray.push('expansion-' + dispatchId);
        return 'cdk-drop-list-' + dispatchId;
      }
    } else if (dispatchId > 0 ) {
      return this.dispatchDropArray.find((x: string) => x.includes(String(dispatchId))) ?? 'dropCreate';
    } else {
      return 'dropCreate';
    }
  }

  public dropDisassociate(event: CdkDragDrop<any>, dispatches: PtdDispatchDetails[]) {
    if (!event.isPointerOverContainer) {
      // console.log('pointer not over container');
      return;
    }
    console.log('disassociating');
    let originArray;
    let originData;
    let originDispatch;
    const splitwarehouse: SplitWarehouse = {};
    console.log(
      'Came from: ',
      event.previousContainer.data[event.previousIndex]
    );
    const sacrificialArray: any[] = [];
    originArray = event.previousContainer.data;
    originData = event.previousContainer.data[event.previousIndex];
    if (!originData?.warehouse) {
      return;
    }
    const warehouse = originData.warehouse;
    originDispatch = dispatches.find((x: PtdDispatchDetails) => x.dispatchId === originData.associatedDispatch);
    const splitsToDisassociate = (originDispatch?.dispatchProformas ?? [originData])
      .filter((item) =>
        item.originPfi === originData.originPfi &&
        (item.warehouse?.warehouseId ?? 0) === (warehouse.warehouseId ?? 0)
      )
      .map((item) => ({
        ...item,
        associatedDispatch: 0,
        comingFromDispatch: originDispatch?.dispatchId,
      }));

    this.applyOptimisticDisassociationState(splitsToDisassociate, originDispatch?.dispatchId ?? null);

    forkJoin(splitsToDisassociate.map((split) =>
      this.proformaService.associateProforma(this.getCurrentContactId(), split)
    ))
      .subscribe({
        next: (result: any) => {
          console.log(result);
          this.scheduleClientRefresh();
          if (!originDispatch) {
            return;
          }
          if (this.isLastDirect(originDispatch.dispatchProformas ?? []) && (originDispatch.dispatchNoLetter?.commodityID === 9 || originDispatch.dispatchNoLetter?.commodityID === 10)) {
            this.updateDirectStatus(originDispatch, undefined as any, 'Last ' + originData.splitCommodity.name + ' part removed, do you want to unmark this dispatch as '+ originData.splitCommodity.name + '?');
          }
        },
        error: (err: any) => {
          console.log(err);
          this.refreshClientFromServer();
        },
        complete: () => {
          console.log("drop-disassociate complete");
        },
      });
  }

  public drop(event: CdkDragDrop<any>, dispatch: PtdDispatchDetails) {
    if (!event.isPointerOverContainer) {
      // console.log('pointer not over container');
      return;
    }
    const splitwarehouse: SplitWarehouse = {};
    const dispatchId = dispatch.dispatchId ?? 0;
    const originData = event.item.data;
    const originArray = event.previousContainer.data;
    if (!originData?.warehouse) {
      return;
    }
    const warehouse = originData.warehouse;

    if (dispatch.closedToIwd) {
      if (
        originData.warehouse.warehouseId !== (dispatch.dispatchWarehouse?.warehouseId ?? 0)
      ) {
        console.log('Nuhuhuuh, can\'t associate to this dispatch');
        return;
      }
    }

    console.log(
      'Came from: ',
      event.previousContainer.data.find((x: any) => x.pfiId === originData.originPfi)
    );
    splitwarehouse.associatedDispatch = dispatchId;
    splitwarehouse.originPfi = originData.originPfi;
    splitwarehouse.warehouseLineCount = originData.warehouseLineCount;
    splitwarehouse.warehouseValue = originData.warehouseValue;
    splitwarehouse.comingFromDispatch = originData.comingFromDispatch;
    const targetWarehouse = warehouse ?? ({ warehouseId: 0, region: 'Unknown' } as any);
    splitwarehouse.warehouse = targetWarehouse;
    (splitwarehouse.warehouse as any).region = targetWarehouse.region ?? 'Unknown';
    splitwarehouse.splitCommodity = originData.splitCommodity;
    console.log("Going to: ", event.container.data[event.currentIndex]);

    const splitElement = document.getElementById("split-" + originData.originPfi + "-" + warehouse.region);
    if (splitElement) {
      splitElement.className = splitElement.className.replace(warehouse.region.toLowerCase(), "working ");
    }

    this.applyOptimisticAssociationState(splitwarehouse, originData?.comingFromDispatch ?? null, dispatchId, originData);

    this.proformaService
      .associateProforma(
        this.getCurrentContactId(),
        splitwarehouse
      )
      .subscribe({
        next: (result: any) => {
          console.log(result);
          if ((originData.splitCommodity.commodityID === 9 || originData.splitCommodity.commodityID === 10) && (dispatch.dispatchNoLetter?.commodityID !== 9 && dispatch.dispatchNoLetter?.commodityID !== 10)) {
            this.updateDirectStatus(dispatch, originData.splitCommodity.commodityID, 'This proforma part is ' + originData.splitCommodity.name + ', do you want to mark this dispatch as '+ originData.splitCommodity.name + '?');
          }
          this.scheduleClientRefresh();
        },
        error: (err: any) => {
          console.log(err);
          this.refreshClientFromServer();
        },
        complete: () => {
          if (splitElement) {
            splitElement.className = splitElement.className.replace("working ", warehouse.region + " ");
          }
          console.log("drop-associate complete");
        },
      });
  }

  shuffle(id: number) {
    if (this.expansionState && !this.expansionState.get(id)) {
      this.expansionState.set(id, true);
      this.stateService.drawerState$.next(this.expansionState);
    } else {
      this.expansionState.set(id, false);
      this.stateService.drawerState$.next(this.expansionState);
    }
  }

  flip(id: number) {
    if (this.flipState && !this.flipState.get(id)) {
      this.flipState.set(id, true);
      this.stateService.flipState$.next(this.flipState);
    } else {
      this.flipState.set(id, false);
      this.stateService.flipState$.next(this.flipState);
    }
  }

  methodShortName(method: FreightMethod) {
    let matches = '';
    const methodName = method.name ?? '';
    if (method.methodId === 1) {
      matches = 'Air';
    } else if (method.methodId === 2) {
      matches = 'Sea';
    } else if (method.methodId === 4) {
      matches = methodName;
    } else if (method.methodId === 70012) {
      matches = 'Virtual';
    } else if (method.methodId === 70014) {
      matches = 'EXW';
    } else {
      matches = methodName.match(/\b(\w)/g)?.join('') ?? methodName;
    }

    return matches;
  }

  changeIWDState(dispatch: PtdDispatchDetails) {
    const iwdClosedSwitch = <HTMLInputElement>(
      document.getElementById('iwdClosedSwitch-' + dispatch.dispatchId)
    );
    if (!iwdClosedSwitch) {
      return;
    }
    const updateDetails: PreDispatchDetails = {};
    updateDetails.closedIwd = iwdClosedSwitch.checked;
    updateDetails.warehouse = dispatch.dispatchWarehouse?.warehouseId ?? 0;
    Utils.updateDispatch(dispatch, updateDetails, this.dispatchService);

    (dispatch.dispatchProformas ?? []).forEach((value) => {
      if (
        value.warehouse?.warehouseId === dispatch.dispatchWarehouse?.warehouseId
      ) {
        // don't move a line that exists in the same warehouse
      } else {
        const splitwarehouse: SplitWarehouse = {};
        splitwarehouse.associatedDispatch = 0;
        splitwarehouse.originPfi = value.originPfi;
        const iwdWarehouse = value.warehouse ?? ({ warehouseId: 0, region: 'Unknown' } as any);
        splitwarehouse.warehouse = iwdWarehouse;
        splitwarehouse.warehouseLineCount = value.warehouseLineCount;
        (splitwarehouse.warehouse as any).region = iwdWarehouse.region ?? 'Unknown';
        splitwarehouse.warehouseValue = value.warehouseValue;
        splitwarehouse.comingFromDispatch = dispatch.dispatchId;

        this.proformaService.associateProforma(this.getCurrentContactId(), splitwarehouse).subscribe({
          next: (result: any) => {
            console.log(result);
            this.scheduleClientRefresh();
          },
          error: (err: any) => {
            console.log(err);
          },
          complete: () => {
            console.log('change-iwd-state complete');
          },
        });
      }
    });
  }


  disassociate(dispatchId: number, dispatches: PtdDispatchDetails[]) {
    const confirmDialogRef  = this.dialog.open(ConfirmationComponent, {
      id: 'confirmation-dialog',
      width: '600px',
      disableClose: true,
      panelClass: 'confirmation-pane',
      data: {
        titleText: 'Are you sure you want to disassociate all items from this dispatch?'
      }
    });

    confirmDialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.dispatchService.disassociateProformas(dispatchId, this.getCurrentContactId()).subscribe({
          next: (result: any) => {
            const originDispatch = dispatches.find((x: PtdDispatchDetails) => x.dispatchId === dispatchId);
            if (originDispatch && (originDispatch.dispatchNoLetter?.commodityID === 9 || originDispatch.dispatchNoLetter?.commodityID === 10)) {
              this.updateDirectStatus(originDispatch, undefined as any, 'Last ' + (originDispatch.dispatchNoLetter?.name ?? 'direct') + ' part removed, do you want to unmark this dispatch as '+ (originDispatch.dispatchNoLetter?.name ?? 'direct') + '?');
            }
            console.log(result);
          },
          error: (err: any) => {
            console.log(err);
          },
          complete: () => {
            console.log('Disassociated');
            this.scheduleClientRefresh();
          }
        });
      }
    })
  }

  getSummary(dispatch: PtdDispatchDetails) {
    this.downloadSummaries.getSummary(dispatch);
  }

  canDisassociate(dispatchProformas: Array<SplitWarehouse>) {
    let canCount = 0;
    dispatchProformas.forEach((split) => {
      if (split.splitPercentRecieved !== 100) {
        canCount++;
      }
    });
    if (canCount > 0) {
      return true;
    } else {
      return false;
    }
  }

  isLastDirect(dispatchProformas: Array<SplitWarehouse>) {
    let canCount = 0;
    dispatchProformas.forEach((split) => {
      if (split.splitCommodity?.commodityID === 10) {
        canCount++;
      }
    });
    if (canCount === 0) {
      return true;
    } else {
      return false;
    }
  }

  canDelete(dispatchProformas:  Array<SplitWarehouse>) {
    let canCount = 0;
    dispatchProformas.forEach((split) => {
      canCount++;
    });
    if (canCount === 0) {
      return true;
    } else {
      return false;
    }
  }

  deleteDispatch(dispatchId: number) {
    const confirmDialogRef  = this.dialog.open(ConfirmationComponent, {
      id: 'delete-dialog',
      width: '600px',
      disableClose: true,
      panelClass: 'delete-pane',
      data: {
        titleText: 'Are you sure you want to delete this dispatch?'
      }
    });

    confirmDialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.dispatchService.deleteDispatch(dispatchId, this.getCurrentContactId()).subscribe({
          next: (result: any) => {
            console.log(result);
            let runloop = true;
            while (runloop) {
              const dropIndex = this.dispatchDropArray.findIndex((x: string) => x.includes(String(dispatchId)));
              if (dropIndex < 0) {
                runloop = false;
              } else{
                this.dispatchDropArray.splice(dropIndex, 1);
              }
            }
          },
          error: (err: any) => {
            console.log(err);
          },
          complete: () => {
            console.log('Deleted');
            this.scheduleClientRefresh();
          }
        })
      }
    })
  }

  /**
   * Handles drop event for creating a new dispatch
   * @param event CdkDragDrop<any> event
   * @param client ClientDetails client object
   * @param route number route number - 1 being dispatch dialog
   */
  dropCreate(event: CdkDragDrop<any>, client: ClientDetails, route: number) {
    // Get data from origin container
    const originArray = event.previousContainer.data;
    const originData: SplitWarehouse = event.item.data;
    // Find proforma in origin container data
    const originProforma: Proforma = event.previousContainer.data.find(
      (x: any) => x.pfiId === originData.originPfi
    );
    if (!originProforma) {
      return;
    }
    const targetArray = event.container.data;
    const transferArrayObject: TransferArrayComponent = new TransferArrayComponent;

    // Set up transfer array
    transferArrayObject.origArray = originArray;
    transferArrayObject.targArray = targetArray;
    transferArrayObject.origIndex = originArray.findIndex(
      (pcItem: any) => pcItem.pfiId === originData.originPfi
    );
    transferArrayObject.targIndex = event.currentIndex;

    // Create dispatch details
    const dispatch: PtdDispatchDetails = {
      modelClient : client.modelClient,
      dispatchWarehouse: originProforma.pfiLeavingFromWarehouse,
      isClosed: false,
      dispatchDeliveryAddress: originProforma.pfiDeliveryAddress,
      dispatchConsigneeAddress: originProforma.pfiConsigneeAddress,
      dispatchFreightMethod: originProforma.pfiFreightMethod,
      incoterm: originProforma.pfiIncoterm,
      incotermLocation: this.getIncotermLocation(originProforma),
      closedToIwd: false,
      dispatchCurrency: originProforma.pfiCurrency,
    };
    (dispatch as any).externalEts =
      (originProforma as any).externalETS ??
      (originProforma as any).externalEts ??
    // If there is only one contact for the proforma, set as dispatch contact
    // if (originProforma.pfiContacts.length === 1) {
    //   dispatch.clientContact = originProforma.pfiContacts[0];
    // }

    // Call sharing service to share data
    this.sharingDispatchData(
      client,
      dispatch,
      route,
      this.getCurrentContactId(),
      originData
    );

    console.log(this.dispatchDropCreateArray, originProforma);
  }

  updateDirectStatus (dispatch: PtdDispatchDetails, category: number | undefined, titleText: string) {
    const confirmDialogRef  = this.dialog.open(ConfirmationComponent, {
      id: 'direct-dialog',
      width: '600px',
      disableClose: true,
      panelClass: 'direct-pane',
      data: {
        titleText: titleText
      }
    });

    confirmDialogRef.afterClosed().subscribe((result) => {
      if (result) {
        const updateDetails: PreDispatchDetails = {};
        updateDetails.shipmentCategory = category;
        updateDetails.warehouse = dispatch.dispatchWarehouse?.warehouseId ?? 0;
        Utils.updateDispatch(dispatch, updateDetails, this.dispatchService);
        this.scheduleClientRefresh();
      }
    })
  }
}
